import { State } from '@/store/modules/global/settings/settings.store';
import { ActionTree, Commit, MutationTree } from 'vuex';
import { Duration } from '@/utils/types/global';
import getDurationRow from '@/utils/time/datetime';
import { EventTypes } from '@/utils/enums/eventTypes';
import {
  EndpointTableSort,
  User,
  CapabilityPermission,
  ResourceGroup,
  CustomPermData,
  LabelKeyType,
  TenantRole,
  FieldValue,
  SetTenantRoleData,
  RemoveTenantRoleData,
  SetCusRowPermData,
  RoleFormData,
  GroupRoleFormData,
  RolesAndGroupRolesFieldGroupData,
  RolesAndGroupRolesFieldData,
  Resource,
} from '@/store/modules/global/settings/settings.interfaces';
import { addWidgetDefulatData } from '@/components/Settings/Custom-DB-query/settings.customQuery.types';
import _ from 'lodash';
import getter from '@/store/modules/global/settings/settings.getter';
let timer: any = null;
interface WidgetFieldInfoType {
  field: string;
  value: any;
}

interface CapabilityPermissionObj {
  capability: string;
  permission: string;
  checked: boolean;
}

const mutations: MutationTree<State> = {
  setDuration(state: State, data: Duration): void {
    localStorage.setItem('durationRow', JSON.stringify(data, null, 0));
    state.durationRow = data;
    const event = new CustomEvent(EventTypes.SS_DURATION_UPDATE, {
      detail: data,
    });
    window.dispatchEvent(event);
  },
  setLicenseDuration(state: State, data: Duration): void {
    localStorage.setItem('durationLicenseRow', JSON.stringify(data, null, 0));
    state.durationLicenseRow = data;
  },
  setCompareDuration(state: State, data: Duration) {
    localStorage.setItem('compareDurationRow', JSON.stringify(data, null, 0));
    state.compareDurationRow = data;
  },
  resetDuration(state: State): void {
    localStorage.removeItem('durationRow');
    state.durationRow = getDurationRow();
  },
  setUtc(state: State, data: string): void {
    state.utc = data;
  },
  setEvents(state: State, data: any[]): void {
    state.eventStack = data;
  },
  setCharts(state: State, data: any[]): void {
    state.chartStack.push(data);
  },
  runEvents(state: State): void {
    clearTimeout(timer);
    timer = setTimeout(
      () =>
        state.eventStack.forEach((event: any) => {
          setTimeout(event(), 0);
        }),
      500,
    );
  },
  setTimezoneOffset(state: State, data: any): void {
    state.timezone = data.resp._source.timezone;
    state.timezoneoffset = data.resp._source.timezone_offset;
  },
  setEdit(state: State, status: boolean): void {
    state.edit = status;
  },
  setTimezones(state: State, data: any) {
    state.timeZones = data.timezones;
  },
  setPeriod(state: State, data: any) {
    state.period = data;
  },
  setCurrentTimezone(state: State, data: any) {
    if (data.timeZone) {
      state.timezone = data.timeZone.value;
      state.timezoneoffset = Number(data.timeZone.zoneId);
    }
  },
  setUpdatedTimezone(state: State, data: any) {
    state.timezone = data.value;
    state.timezoneoffset = Number(data.zoneId);
  },
  setCurrTimeLabel(state: State, data: any) {
    state.currTimeLabel = data;
  },
  setTransConfigType(state: State, data: string) {
    state.transcation.configType = data;
  },
  setTransIsFormOpen(state: State, data: boolean) {
    state.transcation.isFormOpen = data;
  },
  setTransApplicationTableList(state: State, data: Array<Object>) {
    state.transcation.applicationTableList = data;
  },
  setTransEndpointTableList(state: State, data: Array<Object>) {
    state.transcation.endpointTableList = data;
  },
  setTransEpExTableList(state: State, data: Array<Object>) {
    state.transcation.epExcludeTableList = data;
  },
  setCurrentEpTransaction(state: State, data: string) {
    state.transcation.currentEpTransaction = data;
  },
  setTransReloadApplTable(state: State, data: boolean) {
    state.transcation.reloadApplTable = data;
  },
  setTransReloadEpTable(state: State, data: boolean) {
    state.transcation.reloadEpTable = data;
  },
  setTransReloadEpExTable(state: State, data: boolean) {
    state.transcation.reloadEpExTable = data;
  },
  setTransEndpointTableSort(state: State, data: EndpointTableSort) {
    state.transcation.epTableSort = data;
  },
  setTransEpExTableSort(state: State, data: EndpointTableSort) {
    state.transcation.epExTableSort = data;
  },
  setUsers(state: State, data: User[]) {
    state.users = data;
  },
  setCurrentDBType(state: State, data: any) {
    state.currentDBType = data;
  },
  setCurrentDBService(state: State, data: any) {
    state.currentDatabaseService = data;
  },
  setShowQueryForm(state: State, data: boolean) {
    state.showQueryForm = data;
  },
  setShowQueryTable(state: State, data: boolean) {
    state.showQueryTable = data;
  },
  setShowRunQueryTable(state: State, data: boolean) {
    state.showRunQueryTable = data;
  },
  setQueryTableData(state: State, data: any) {
    state.queryTableData = {
      total: state.queryTableData.total || 0,
      records: [...(state.queryTableData.records || []), ...data.records],
    };
  },
  setQueryFormInfoFields(state: State, data: WidgetFieldInfoType) {
    state.addWidgetData.queryFormInfo[data.field] = data.value;
  },
  setAddDashboardInfoFields(state: State, data: WidgetFieldInfoType) {
    state.addWidgetData.addDashboardInfo[data.field] = data.value;
  },
  setWidgetFormData(state: State, data: WidgetFieldInfoType) {
    state.addWidgetData.widgetFormData[data.field] = data.value;
  },
  resetAddCustomWidgetData(state: State, data: string) {
    if (data === 'addDashboardInfo') {
      const dashboardData = _.cloneDeep(addWidgetDefulatData[data]);
      dashboardData.widgetType =
        state.addWidgetData.addDashboardInfo.widgetType;
      dashboardData.metricName = state.addWidgetData.queryFormInfo.metricName;
      dashboardData.widgetName =
        state.addWidgetData.addDashboardInfo.widgetName;
      state.addWidgetData.addDashboardInfo = dashboardData;
    }
    if (data === 'widgetFormData') {
      const formData = _.cloneDeep(addWidgetDefulatData[data]);
      state.addWidgetData.widgetFormData = formData;
    }
  },
  setActiveTimeLabel(state: State, data: any) {
    state.activeTimeLabel = data;
  },
  setIsCompareOptionsSelected(state: State, data: Boolean) {
    state.isCompareOptionsSelected = data;
  },
  setIsCompareCustom(state: State, data: Boolean) {
    state.isCompareCustom = data;
  },
  setDisablePrevious(state: State, data: Boolean) {
    state.disablePrevious = data;
  },
  setRoleFormData(state: State, data: RoleFormData) {
    state.roleFormData = data;
  },
  setCapPermInfo(state: State, data: CapabilityPermission[]) {
    state.capPermInfo = data;
  },
  setRoleFormName(state: State, data: string) {
    state.roleFormData.roleName = data;
  },
  addCapabilityToRoleForm(state: State, data: ResourceGroup) {
    state.roleFormData.resourceGroups.push(data);
  },
  removeCapabilityFromRoleForm(state: State, data: string) {
    state.roleFormData.resourceGroups =
      state.roleFormData.resourceGroups.filter(
        (obj) => obj.resourceGroup != data,
      );
  },
  setCapabilityPermission(state: State, data: CapabilityPermissionObj) {
    const resourceGroups = state.roleFormData.resourceGroups;

    for (let i = 0; i < resourceGroups.length; i++) {
      if (resourceGroups[i].resourceGroup == data.capability) {
        if (!data.checked) {
          resourceGroups[i].permissions = resourceGroups[i].permissions.filter(
            (perm) => perm != data.permission,
          );
        } else {
          resourceGroups[i].permissions.push(data.permission);
        }
        break;
      }
    }
  },
  setCustomRolePermData(state: State, data: CustomPermData) {
    state.customPermData = data;
  },
  setCusRoleDataToRoleForm(state: State) {
    const cusData = state.customPermData;
    cusData.resources = cusData.resources.filter(
      (obj) => obj.permissions?.length > 0,
    );
    const capabilities = state.roleFormData.resourceGroups;

    for (let i = 0; i < capabilities.length; i++) {
      if (capabilities[i].resourceGroup == cusData.currCapability) {
        state.roleFormData.resourceGroups[i] = _.cloneDeep({
          isCustomised: true,
          permissions: state.roleFormData.resourceGroups[i].permissions,
          resourceGroup: cusData.currCapability,
          resources: cusData.resources,
        });
        break;
      }
    }

    state.customPermData = {
      currCapability: '',
      resources: [],
      showCusView: false,
      permissions: [],
    };
  },
  resetRoleFormData(state: State) {
    state.roleFormData = { roleName: '', resourceGroups: [] };
    state.customPermData = {
      currCapability: '',
      resources: [],
      showCusView: false,
      permissions: [],
    };
  },
  resetRoleFormCapabilityData(state: State, data: ResourceGroup) {
    const capabilities = state.roleFormData.resourceGroups;

    for (let i = 0; i < capabilities.length; i++) {
      if (capabilities[i].resourceGroup == data.resourceGroup) {
        state.roleFormData.resourceGroups[i] = data;
        break;
      }
    }
  },
  setCusRowPermData(state: State, data: SetCusRowPermData) {
    if (
      data.isChecked &&
      !getter.getCustomPermResourcesMapper(state)[data.serviceId]
    ) {
      let resources = [...(state.customPermData.resources || [])];
      resources.push({
        resource: data.serviceId,
        permissions: [data.permKey],
        resourceType: data.resourceType,
      });
      state.customPermData.resources = resources;
    } else {
      const resources = state.customPermData.resources;
      const modifiedResources: Resource[] = [];
      for (let i = 0; i < resources.length; i++) {
        if (resources[i].resource == data.serviceId) {
          let permList = data.isChecked
            ? [...resources[i].permissions, data.permKey]
            : resources[i].permissions?.filter((perm) => perm != data.permKey);
          if (permList.length > 0)
            modifiedResources.push({
              resource: data.serviceId,
              permissions: permList,
              resourceType: data.resourceType,
            });
        } else modifiedResources.push(resources[i]);
      }
      state.customPermData.resources = modifiedResources;
    }
  },
  setCusColPermData(state: State, data: any) {
    let resources = _.cloneDeep(state.customPermData.resources);
    const servicesList = data.servicesList;

    for (let i = 0; i < servicesList.length; i++) {
      if (servicesList[i][data.permKey] === data.isChecked) continue;
      let permissions =
        getter.getCustomPermResourcesMapper(state)[servicesList[i].key];
      if (data.isChecked && !permissions) {
        resources.push({
          resource: servicesList[i].key,
          permissions: [data.permKey],
          resourceType: data.resourceType,
        });
      } else if (
        data.isChecked
          ? !permissions.includes(data.permKey)
          : permissions.includes(data.permKey)
      ) {
        for (let resource of resources) {
          if (resource.resource == servicesList[i].key) {
            let permList = data.isChecked
              ? [...resource.permissions, data.permKey]
              : resource.permissions?.filter((perm) => perm != data.permKey);
            resource.permissions = permList;
          }
        }
      }
    }
    state.customPermData.resources = resources;
  },
  setRoleTenant(state: State, data: LabelKeyType) {
    state.roleTenant = data;
  },
  setGroupRoleFormData(state: State, data: GroupRoleFormData) {
    state.groupRoleFormData = data;
  },
  setGroupRoleFormName(state: State, data: FieldValue) {
    state.groupRoleFormData[data.field] = data.value;
  },
  setGroupRoleFormTenantData(state: State, data: TenantRole) {
    const tenantRoles =
      data.type == 'userForm'
        ? state.rolesAndGroupRolesFieldData.tenantRoles
        : state.groupRoleFormData.tenantRoles;
    tenantRoles.push(data);
  },
  removeGroupRoleFormTenantData(state: State, data: any) {
    if (data.type == 'userForm') {
      state.rolesAndGroupRolesFieldData.tenantRoles =
        state.rolesAndGroupRolesFieldData.tenantRoles.filter(
          (obj) => obj.key != data.value,
        );
    } else {
      state.groupRoleFormData.tenantRoles =
        state.groupRoleFormData.tenantRoles.filter(
          (obj) => obj.key != data.value,
        );
    }
  },
  setGroupRoleFormRoleData(state: State, data: SetTenantRoleData) {
    const tenantRoles =
      data.type == 'userForm'
        ? state.rolesAndGroupRolesFieldData.tenantRoles
        : state.groupRoleFormData.tenantRoles;
    tenantRoles
      .find((obj) => obj.key == data.tenantId)
      ?.roles.push(data.roleData);
  },
  removeGroupRoleFormRoleData(state: State, data: RemoveTenantRoleData) {
    const tenantRoles =
      data.type == 'userForm'
        ? state.rolesAndGroupRolesFieldData.tenantRoles
        : state.groupRoleFormData.tenantRoles;
    const tenant = tenantRoles.find((obj) => obj.key == data.tenantId);
    if (tenant?.roles) {
      tenant.roles = tenant?.roles.filter((obj) => obj.key != data.roleId);
    }
  },
  setUserFormFieldData(state: State, data: FieldValue) {
    state.userFormData[data.field] = data.value;
  },
  setRolesAndGroupRolesFieldGroupData(
    state: State,
    data: RolesAndGroupRolesFieldGroupData,
  ) {
    state.rolesAndGroupRolesFieldData.groupRoles.push(data);
  },
  removeRolesAndGroupRolesFieldGroupData(state: State, data: string) {
    state.rolesAndGroupRolesFieldData.groupRoles =
      state.rolesAndGroupRolesFieldData.groupRoles.filter(
        (obj) => obj.key != data,
      );
  },
  setRolesAndGroupRolesFieldIsGroupRole(state: State, data: boolean) {
    state.rolesAndGroupRolesFieldData.isGroupRole = data;
  },
  setRolesAndGroupRolesFieldData(
    state: State,
    data: RolesAndGroupRolesFieldData,
  ) {
    state.rolesAndGroupRolesFieldData = data;
  },
  setReloadTable(state: State, data: boolean) {
    state.reloadTable = data;
  },
  setCustomRange(state: State, data: any) {
    state.customRange = data;
  },
};
export default mutations;
